import { createContext, useContext, useEffect, useState } from 'react';
import {
  getActiveHealthBenefits,
  getActiveLifeAndDisabilityInsurances,
  getActiveSpendingAccounts,
  getActiveLifestyleBenefits,
  getFamilyMembers
} from '../services/enrollment';
import { Profile } from '../types';
import { COBRA_PERSONAS } from '../utils/utils';
import { AuthStore } from './auth';

export type EnrollmentState = {
  activeHealthBenefits: Array<any>;
  activeSpendingAccounts: Array<any>;
  activeLifeAndDisabilityInsurances: Array<any>;
  activeLifestyleBenefits: Array<any>;
  familyMembers: Array<any>;
};

export const EnrollmentStore = createContext<any>({
  activeHealthBenefits: [],
  activeSpendingAccounts: [],
  activeLifeAndDisabilityInsurances: [],
  activeLifestyleBenefits: [],
  updateCost: (type: string, planId: string, value: number) => {},
  updateSelectedProfiles: (type: string, profile: Profile) => {},
  familyMembers: [],
  addFamilyMember: (data: any) => {},
});

const { Provider } = EnrollmentStore;

export const EnrollmentProvider = ({ children }: any) => {
  const [, , , selectedPersona] = useContext(AuthStore) || [];
  const [currentCost, setCurrentCost] = useState<number>(0);
  const [updatedCost, setUpdatedCost] = useState<number>(0);
  const [enrollmentState, setEnrollmentState] = useState<EnrollmentState>({
    activeHealthBenefits: [],
    activeSpendingAccounts: [],
    activeLifeAndDisabilityInsurances: [],
    activeLifestyleBenefits: [],
    familyMembers: [],
  });
  const [medicalSelectedProfiles, setMedicalSelectedProfiles] = useState<
    Profile[]
  >([]);

  const [dentalSelectedProfiles, setDentalSelectedProfiles] = useState<
    Profile[]
  >([]);

  const [accidentInsuranceSelectedProfiles, setAccidentInsuranceSelectedProfiles] = useState<
    Profile[]
  >([]);

  const [visionSelectedProfiles, setVisionSelectedProfiles] = useState<
    Profile[]
  >([]);

  const [criticalIlnessSelectedProfiles, setCriticalIlnessSelectedProfiles] =
    useState<Profile[]>([]);

  useEffect(() => {
    let activeHealthBenefits = getActiveHealthBenefits(selectedPersona);
    let activeSpendingAccounts = getActiveSpendingAccounts(selectedPersona);
    let activeLifeAndDisabilityInsurances =
      getActiveLifeAndDisabilityInsurances();
    let activeLifestyleBenefits = getActiveLifestyleBenefits();
    let familyMembers = getFamilyMembers();

    let activeMedicalProfiles = activeHealthBenefits.find(
      (e) => e.type === 'medical'
    );

    setMedicalSelectedProfiles(activeMedicalProfiles.profiles);

    let activeDentalProfiles = activeHealthBenefits.find(
      (e: any) => e.type === 'dental'
    );

    const currentlySelectedDentalProfiles =
      activeDentalProfiles.profiles.filter((e: any) => e.id !== '4');

    setDentalSelectedProfiles(currentlySelectedDentalProfiles);

    let activeAccidentInsuranceProfiles = activeHealthBenefits.find(
      (e: any) => e.type === 'accident-insurance'
    );

    const currentlySelectedAccidentInsuranceProfiles =
      activeAccidentInsuranceProfiles?.profiles.filter((e: any) => e.id !== '4');

    setAccidentInsuranceSelectedProfiles(currentlySelectedAccidentInsuranceProfiles);

    let activeVisionProfiles = activeHealthBenefits.find(
      (e: any) => e.type === 'vision'
    );

    const currentlySelectedVisionProfiles =
      activeVisionProfiles.profiles.filter(
        (e: any) => e.id !== '4' && e.id !== '3'
      );

    setVisionSelectedProfiles(currentlySelectedVisionProfiles);

    let activeCriticalIlnessProfiles = activeHealthBenefits.find(
      (e: any) => e.type === 'critical-illness'
    );

    const nonChildProfiles = activeCriticalIlnessProfiles?.profiles.filter(
      (e: any) => e.relationship !== 'child'
    );
    const childProfiles = activeCriticalIlnessProfiles?.profiles.filter(
      (e: any) => e.relationship === 'child'
    );

    if (activeCriticalIlnessProfiles) setCriticalIlnessSelectedProfiles([...nonChildProfiles, childProfiles]);

    if (COBRA_PERSONAS.indexOf(selectedPersona) > -1) {
      setMedicalSelectedProfiles([]);
      setDentalSelectedProfiles([]);
      setAccidentInsuranceSelectedProfiles([]);
      setVisionSelectedProfiles([]);
      activeLifeAndDisabilityInsurances = [];
      activeLifestyleBenefits = [];
    }

    setEnrollmentState({
      activeHealthBenefits,
      activeSpendingAccounts,
      activeLifeAndDisabilityInsurances,
      activeLifestyleBenefits,
      familyMembers,
    });
  }, []);

  const updateCost = (type: string, plan: any) => {
    if (
      [
        'medical',
        'dental',
        'critical-illness',
        'accident-insurance',
        'hospital-indemnity',
        'vision',
        'spousal-surcharge',
        'non-tobacco-user-credit',
        'health-savings',
      ].indexOf(type) > -1
    ) {
      const { activeHealthBenefits } = enrollmentState;
      const currentBenefit = activeHealthBenefits.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.cost = plan.cost;
      currentBenefit.plan.id = plan.id;
      currentBenefit.plan.costType = plan.costType;

      setEnrollmentState({
        ...enrollmentState,
        activeHealthBenefits: [...activeHealthBenefits],
      });
    }

    if (
      ['legal-services'].indexOf(
        type
      ) > -1
    ) {
      const { activeLifestyleBenefits } = enrollmentState;
      const currentBenefit = activeLifestyleBenefits.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.cost = plan.cost;
      currentBenefit.plan.id = plan.id;
      currentBenefit.plan.costType = plan.costType;

      setEnrollmentState({
        ...enrollmentState,
        activeLifestyleBenefits: [...activeLifestyleBenefits],
      });
    }

    if (['health-care', 'dependant-care'].indexOf(type) > -1) {
      const { activeSpendingAccounts } = enrollmentState;
      const currentBenefit = activeSpendingAccounts.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.cost = plan.cost;
      currentBenefit.plan.id = plan.id;
      currentBenefit.plan.costType = plan.costType;

      setEnrollmentState({
        ...enrollmentState,
        activeSpendingAccounts: [...activeSpendingAccounts],
      });
    }

    if (
      [
        'base-life',
        'optional-life',
        'spouse-life',
        'long-term-disability',
      ].indexOf(type) > -1
    ) {
      const { activeLifeAndDisabilityInsurances } = enrollmentState;
      const currentBenefit = activeLifeAndDisabilityInsurances.find(
        (benefit: any) => {
          return benefit.type === type;
        }
      );

      currentBenefit.plan.cost = plan.cost;
      currentBenefit.plan.id = plan.id;
      currentBenefit.plan.costType = plan.costType;

      setEnrollmentState({
        ...enrollmentState,
        activeLifeAndDisabilityInsurances: [
          ...activeLifeAndDisabilityInsurances,
        ],
      });
    }
  };

  useEffect(() => {
    const {
      activeHealthBenefits,
      activeLifestyleBenefits,
      activeSpendingAccounts,
      activeLifeAndDisabilityInsurances,
    } = enrollmentState;
    const ahCosts = activeHealthBenefits
      .map((benefit: any) => benefit.plan.cost)
      .reduce((acc: number, curr: number) => {
        acc += curr < 0 ? 0 : curr;
        return acc;
      }, 0);

    const avCost = activeLifestyleBenefits
      .map((benefit: any) => benefit.plan.cost)
      .reduce((acc: number, curr: number) => {
        acc += curr;
        return acc;
      }, 0);

    const asCost = activeSpendingAccounts
      .map((benefit: any) => benefit.plan.cost)
      .reduce((acc: number, curr: number) => {
        acc += curr;
        return acc;
      }, 0);

    const alCost = activeLifeAndDisabilityInsurances
      .map((benefit: any) => benefit.plan.cost)
      .reduce((acc: number, curr: number) => {
        acc += curr;
        return acc;
      }, 0);

    if (!currentCost) {
      setCurrentCost(ahCosts + avCost + asCost + alCost);
    }

    setUpdatedCost(ahCosts + avCost + asCost + alCost);
  }, [currentCost, enrollmentState]);

  const updateSelectedProfiles = (type: string, profile: Profile) => {
    if (type === 'medical') {
      const alreadySelectedProfile = medicalSelectedProfiles.find(
        (e) => e.id === profile.id
      );

      if (!alreadySelectedProfile) {
        setMedicalSelectedProfiles([...medicalSelectedProfiles, profile]);
      }

      if (alreadySelectedProfile) {
        const filteredProfiles = medicalSelectedProfiles.filter(
          (e) => e.id !== profile.id
        );
        setMedicalSelectedProfiles(filteredProfiles);
      }

      return undefined;
    } else if (type === 'dental') {
      const alreadySelectedProfile = dentalSelectedProfiles.find(
        (e) => e.id === profile.id
      );

      if (!alreadySelectedProfile) {
        setDentalSelectedProfiles([...dentalSelectedProfiles, profile]);
      }

      if (alreadySelectedProfile) {
        const filteredProfiles = dentalSelectedProfiles.filter(
          (e) => e.id !== profile.id
        );
        setDentalSelectedProfiles(filteredProfiles);
      }

      return undefined;
    } else if (type === 'accident-insurance') {
      const alreadySelectedProfile = accidentInsuranceSelectedProfiles.find(
        (e) => e.id === profile.id
      );

      if (!alreadySelectedProfile) {
        setAccidentInsuranceSelectedProfiles([...accidentInsuranceSelectedProfiles, profile]);
      }

      if (alreadySelectedProfile) {
        const filteredProfiles = accidentInsuranceSelectedProfiles.filter(
          (e) => e.id !== profile.id
        );
        setAccidentInsuranceSelectedProfiles(filteredProfiles);
      }

      return undefined;
    } else if (type === 'vision') {
      const alreadySelectedProfile = visionSelectedProfiles.find(
        (e) => e.id === profile.id
      );

      if (!alreadySelectedProfile) {
        setVisionSelectedProfiles([...visionSelectedProfiles, profile]);
      }

      if (alreadySelectedProfile) {
        const filteredProfiles = visionSelectedProfiles.filter(
          (e) => e.id !== profile.id
        );
        setVisionSelectedProfiles(filteredProfiles);
      }

      return undefined;
    } else if (type === 'critical-ilness') {
      const alreadySelectedProfile = criticalIlnessSelectedProfiles.find(
        (e) => e.id === profile.id
      );

      if (!alreadySelectedProfile) {
        setCriticalIlnessSelectedProfiles([
          ...criticalIlnessSelectedProfiles,
          profile,
        ]);
      }

      if (alreadySelectedProfile) {
        const filteredProfiles = criticalIlnessSelectedProfiles.filter(
          (e) => e.id !== profile.id
        );
        setCriticalIlnessSelectedProfiles(filteredProfiles);
      }

      return undefined;
    }
  };

  const updatePlanName = (type: string, plan: any) => {
    if (
      [
        'medical',
        'dental',
        'critical-illness',
        'accident-insurance',
        'hospital-indemnity',
        'vision',
        'spousal-surcharge',
        'non-tobacco-user-credit',
        'health-savings',
      ].indexOf(type) > -1
    ) {
      const { activeHealthBenefits } = enrollmentState;
      const currentBenefit = activeHealthBenefits.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.title = plan.title;

      setEnrollmentState({
        ...enrollmentState,
        activeHealthBenefits: [...activeHealthBenefits],
      });
    }

    if (
      ['legal-services'].indexOf(
        type
      ) > -1
    ) {
      const { activeLifestyleBenefits } = enrollmentState;
      const currentBenefit = activeLifestyleBenefits.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.title = plan.title;

      setEnrollmentState({
        ...enrollmentState,
        activeLifestyleBenefits: [...activeLifestyleBenefits],
      });
    }

    if (['health-care', 'dependant-care'].indexOf(type) > -1) {
      const { activeSpendingAccounts } = enrollmentState;
      const currentBenefit = activeSpendingAccounts.find((benefit: any) => {
        return benefit.type === type;
      });

      currentBenefit.plan.title = plan.title;

      setEnrollmentState({
        ...enrollmentState,
        activeSpendingAccounts: [...activeSpendingAccounts],
      });
    }

    if (
      [
        'base-life',
        'optional-life',
        'spouse-life',
        'long-term-disability',
      ].indexOf(type) > -1
    ) {
      const { activeLifeAndDisabilityInsurances } = enrollmentState;
      const currentBenefit = activeLifeAndDisabilityInsurances.find(
        (benefit: any) => {
          return benefit.type === type;
        }
      );

      currentBenefit.plan.title = plan.title;

      setEnrollmentState({
        ...enrollmentState,
        activeLifeAndDisabilityInsurances: [
          ...activeLifeAndDisabilityInsurances,
        ],
      });
    }
  };

  const addFamilyMember = (data: any) => {
    const { familyMembers, activeHealthBenefits } = enrollmentState;

    const profile = {
      ...data,
      id: (familyMembers.length + 1).toString(),
      isVerified: false,
      dateOfBirth: new Date(data.dob).getTime(),
      name: `${data.firstName} ${data.lastName}`,
      covers: [],
      address: {
        address1: data.address1,
        address2: data.address2,
        city: data.city,
        state: data.state,
        zipcode: data.zipcode,
      },
    };

    const medical = activeHealthBenefits.find((b: any) => b.type === 'medical');
    const dental = activeHealthBenefits.find((b: any) => b.type === 'dental');
    const accidentInsurance = activeHealthBenefits.find((b: any) => b.type === 'accident-insurance');
    const vision = activeHealthBenefits.find((b: any) => b.type === 'vision');
    medical.profiles = [...medical.profiles, profile];
    medical.profileIds = [...medical.profileIds, profile.id];
    dental.profiles = [...dental.profiles, profile];
    dental.profileIds = [...dental.profileIds, profile.id];
    accidentInsurance.profiles = [...accidentInsurance.profiles, profile];
    accidentInsurance.profileIds = [...accidentInsurance.profileIds, profile.id];
    vision.profiles = [...vision.profiles, profile];
    vision.profileIds = [...vision.profileIds, profile.id];

    setEnrollmentState({
      ...enrollmentState,
      activeHealthBenefits,
      familyMembers: [...familyMembers, profile],
    });
  };

  return (
    <Provider
      value={{
        ...enrollmentState,
        currentCost,
        updatedCost,
        updateCost,
        updatePlanName,
        medicalSelectedProfiles,
        dentalSelectedProfiles,
        accidentInsuranceSelectedProfiles,
        visionSelectedProfiles,
        criticalIlnessSelectedProfiles,
        updateSelectedProfiles,
        addFamilyMember,
      }}
    >
      {children}
    </Provider>
  );
};
